/**
 * Maps Partner purchase-order / vendor-order detail payloads (flexible keys)
 * toward MES order-detail UI. Adjust keys when Partner BE schema is confirmed.
 */

export type PartnerOrderPartLineUi = {
  key: string;
  part: string;
  workflow: string;
  qty: number;
  batchName: string | null;
  jobTags: string[];
  done: number;
  total: number;
};

export type PartnerOrderJobUi = {
  key: string;
  name: string;
  status: "Open" | "In Progress" | "Draft";
  tasksDone: number;
  tasksTotal: number;
  recipe: string;
};

export type PartnerOrderDetailUi = {
  company: string;
  contact: string;
  email: string;
  location: string;
  /** Dates from detail (issued / dispatch) when present */
  issuedAt: string | Date | null;
  dispatchDate: string | Date | null;
  partLines: PartnerOrderPartLineUi[];
  jobs: PartnerOrderJobUi[];
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

/** Narrow unknown nested objects from Partner line payloads for safe property reads */
function asRecord(v: unknown): Record<string, unknown> | null {
  if (v != null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

/** Billing / invoice address on PO or nested under `client` (Partner APIs vary). */
function pickBillingAddress(root: Record<string, unknown>): Record<string, unknown> | null {
  const direct =
    asRecord(root.billing_address) ??
    asRecord(root.billingAddress) ??
    asRecord(root.invoice_address) ??
    asRecord(root.invoiceAddress);
  if (direct) return direct;

  const client = asRecord(root.client);
  if (client) {
    return (
      asRecord(client.billing_address) ??
      asRecord(client.billingAddress) ??
      asRecord(client.address) ??
      null
    );
  }
  return null;
}

/**
 * Truventor Partner vendor_order uses `billing_address.address_details` for contact name, email, phone
 * (see truventor-partner-fe active-purchase-order templates).
 */
function billingLeafRecord(billing: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!billing) return null;
  const ad =
    asRecord(billing.address_details) ??
    asRecord(billing.addressDetails) ??
    asRecord(billing.address);
  if (ad && (str(ad.email) || str(ad.name) || str(ad.city))) return ad;
  return billing;
}

/** Human-readable location from billing_address (city, lines, country, etc.). */
function formatBillingLocation(billing: Record<string, unknown> | null): string {
  if (!billing) return "";
  const pre = str(
    billing.address ??
      billing.full_address ??
      billing.formatted_address ??
      billing.formattedAddress ??
      billing.location ??
      billing.display_address,
  );
  if (pre) return pre;

  const line1 = str(
    billing.address_line_1 ??
      billing.address_line1 ??
      billing.line1 ??
      billing.street ??
      billing.street_address,
  );
  const line2 = str(billing.address_line_2 ?? billing.address_line2 ?? billing.line2);
  const city = str(billing.city ?? billing.town);
  const state = str(billing.state ?? billing.region ?? billing.province);
  const postal = str(
    billing.postal_code ??
      billing.zip ??
      billing.zipcode ??
      billing.pincode ??
      billing.pin ??
      billing.pin_code ??
      billing.post_code,
  );
  const country = str(billing.country ?? billing.country_name ?? billing.countryName);

  return [line1, line2, city, state, postal, country].filter(Boolean).join(", ");
}

function pickRoot(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== "object") return {};
  const o = payload as Record<string, unknown>;
  const inner =
    o.data && typeof o.data === "object" && !Array.isArray(o.data)
      ? (o.data as Record<string, unknown>)
      : null;
  const po =
    inner?.purchase_order ??
    inner?.purchaseOrder ??
    inner?.vendor_order ??
    inner?.vendorOrder;
  if (po && typeof po === "object") return po as Record<string, unknown>;
  if (inner) return inner;
  const poTop =
    o.purchase_order ?? o.purchaseOrder ?? o.vendor_order ?? o.vendorOrder;
  if (poTop && typeof poTop === "object") return poTop as Record<string, unknown>;
  return o;
}

/** PO / vendor-order records to scan when lines & client live under nested keys (`data`, nested PO, etc.). */
function orderedDetailRoots(payload: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const push = (x: unknown) => {
    const rec = asRecord(x);
    if (rec && !out.includes(rec)) out.push(rec);
  };

  if (!payload || typeof payload !== "object") return out;
  const o = payload as Record<string, unknown>;

  push(pickRoot(payload));

  push(o);
  const data = asRecord(o.data);
  if (data) {
    push(data);
    push(data.purchase_order);
    push(data.purchaseOrder);
    push(data.vendor_order);
    push(data.vendorOrder);
    push(data.order);
    push(data.po);
  }
  push(o.purchase_order);
  push(o.purchaseOrder);
  push(o.vendor_order);
  push(o.vendorOrder);

  const primary = out[0];
  if (primary) {
    push(asRecord(primary.purchase_order));
    push(asRecord(primary.purchaseOrder));
    push(asRecord(primary.vendor_order));
    push(asRecord(primary.vendorOrder));
  }

  return out;
}

const LINE_ARRAY_KEYS = [
  /** Partner Angular app: GET vendor_orders/:id → `vendor_order_items` (parts + model_attributes.file_name) */
  "vendor_order_items",
  "vendorOrderItems",
  "purchase_order_lines",
  "purchaseOrderLines",
  "purchase_order_line_items",
  "purchaseOrderLineItems",
  "vendor_order_lines",
  "vendorOrderLines",
  "po_lines",
  "poLines",
  "line_items",
  "lineItems",
  "lines",
  "order_lines",
  "orderLines",
  "order_details",
  "orderDetails",
  "items",
  "parts",
  "details",
] as const;

const JOB_ARRAY_KEYS = [
  "jobs",
  "order_jobs",
  "orderJobs",
  "mes_jobs",
  "mesJobs",
  "production_jobs",
  "productionJobs",
  "vendor_jobs",
  "vendorJobs",
] as const;

function findLinesFromRoots(roots: Record<string, unknown>[]): unknown[] {
  for (const root of roots) {
    for (const key of LINE_ARRAY_KEYS) {
      const v = root[key];
      if (Array.isArray(v) && v.length > 0) return v;
    }
  }
  return [];
}

/** Raw Partner PO line records (list row or detail payload). */
export function extractPartnerOrderLineRecords(payload: unknown): Record<string, unknown>[] {
  const roots = orderedDetailRoots(payload);
  const lines = findLinesFromRoots(roots);
  return lines
    .map((line) => (line && typeof line === "object" ? (line as Record<string, unknown>) : null))
    .filter((line): line is Record<string, unknown> => line != null);
}

function findJobsFromRoots(roots: Record<string, unknown>[]): unknown[] {
  for (const root of roots) {
    for (const key of JOB_ARRAY_KEYS) {
      const v = root[key];
      if (Array.isArray(v) && v.length > 0) return v;
    }
  }
  return [];
}

function pickBillingFromLayers(payload: unknown): Record<string, unknown> | null {
  let fallback: Record<string, unknown> | null = null;
  for (const r of orderedDetailRoots(payload)) {
    const b = pickBillingAddress(r);
    if (!b) continue;
    if (!fallback) fallback = b;
    const leaf = billingLeafRecord(b);
    if (
      str(leaf?.email) ||
      formatBillingLocation(leaf) ||
      str(b.email) ||
      formatBillingLocation(b)
    ) {
      return b;
    }
  }
  return fallback;
}

function pickClientFromLayers(payload: unknown): Record<string, unknown> | null {
  for (const r of orderedDetailRoots(payload)) {
    const c = asRecord(r.client);
    if (c && Object.keys(c).length > 0) return c;
  }
  return null;
}

function firstDefinedInRoots(
  roots: Record<string, unknown>[],
  keys: string[],
): unknown {
  for (const r of roots) {
    for (const k of keys) {
      const v = r[k];
      if (v != null && v !== "") return v;
    }
  }
  return null;
}

/** Partner vendor_order root field — buyer company label (string). */
function companyDetailsFromRoots(roots: Record<string, unknown>[]): string {
  for (const r of roots) {
    const cd = str(r.company_details ?? r.companyDetails);
    if (cd) return cd;
  }
  return "";
}

/**
 * Buyer contact: `contact_person_details.name`, else billing contact label on
 * `billing_address.address_details.name` (same as Partner FE order header).
 */
function contactPersonFromRoots(roots: Record<string, unknown>[]): string {
  for (const r of roots) {
    const cp =
      asRecord(r.contact_person_details) ?? asRecord(r.contactPersonDetails);
    const fromCp = str(cp?.name);
    if (fromCp) return fromCp;
    const wrap = pickBillingAddress(r);
    const leaf = billingLeafRecord(wrap);
    const fromBilling = str(leaf?.name);
    if (fromBilling) return fromBilling;
  }
  return "";
}

function contactPersonEmailFromRoots(roots: Record<string, unknown>[]): string {
  for (const r of roots) {
    const cp =
      asRecord(r.contact_person_details) ?? asRecord(r.contactPersonDetails);
    const e = str(cp?.email);
    if (e) return e;
  }
  return "";
}

function mapJobStatus(s: string): "Open" | "In Progress" | "Draft" {
  const u = s.toLowerCase();
  if (u.includes("progress")) return "In Progress";
  if (u.includes("draft")) return "Draft";
  return "Open";
}

export function mapPartnerOrderDetailPayload(payload: unknown): PartnerOrderDetailUi {
  const roots = orderedDetailRoots(payload);
  const root = pickRoot(payload);

  const buyer =
    root.buyer ??
    root.customer ??
    root.organization ??
    root.vendor;
  const buyerObj =
    buyer && typeof buyer === "object" ? (buyer as Record<string, unknown>) : null;

  /** Prefer explicit `client` from any nested layer (`data`, nested PO, etc.). */
  const clientObj =
    pickClientFromLayers(payload) ??
    asRecord(root.client) ??
    buyerObj ??
    asRecord(root.customer) ??
    asRecord(root.organization);

  const billingWrap = pickBillingFromLayers(payload);
  const billingLeaf = billingLeafRecord(billingWrap);

  const company = str(
    companyDetailsFromRoots(roots) ||
      clientObj?.company_name ||
      clientObj?.companyName ||
      clientObj?.organization_name ||
      clientObj?.organizationName ||
      root.buyer_name ||
      root.company_name ||
      root.companyName ||
      buyerObj?.company_name ||
      buyerObj?.companyName ||
      buyerObj?.name ||
      root.vendor_name,
  );

  const contact = str(
    contactPersonFromRoots(roots) ||
      clientObj?.contact_name ||
      clientObj?.contactName ||
      clientObj?.primary_contact_name ||
      clientObj?.contact_person ||
      root.contact_name ||
      root.contactName ||
      buyerObj?.contact_name ||
      buyerObj?.contactName ||
      "",
  );

  const email = str(
    contactPersonEmailFromRoots(roots) ||
      billingLeaf?.email ||
      billingWrap?.email ||
      billingLeaf?.contact_email ||
      billingWrap?.contact_email ||
      billingWrap?.contactEmail ||
      root.buyer_email ||
      root.email ||
      root.contact_email ||
      clientObj?.email ||
      buyerObj?.email ||
      buyerObj?.contact_email,
  );

  const location =
    formatBillingLocation(billingLeaf) ||
    formatBillingLocation(billingWrap) ||
    str(root.location) ||
    [
      billingLeaf?.city ?? billingWrap?.city ?? root.city ?? clientObj?.city ?? buyerObj?.city,
      billingLeaf?.state ?? billingWrap?.state ?? root.state ?? clientObj?.state ?? buyerObj?.state,
      billingLeaf?.country ??
        billingWrap?.country ??
        root.country ??
        clientObj?.country ??
        buyerObj?.country,
    ]
      .filter(Boolean)
      .map(String)
      .join(", ") ||
    str(root.address ?? clientObj?.address ?? buyerObj?.address);

  const issuedAt = firstDefinedInRoots(roots, [
    "issued_at",
    "issuedAt",
    "issue_date",
    "created_at",
    "createdAt",
  ]) as string | Date | null;

  const dispatchDate = firstDefinedInRoots(roots, [
    "dispatch_date",
    "dispatchDate",
    "dispatch_at",
    "expected_delivery_date",
    "expectedDeliveryDate",
    "due_date",
    "dueDate",
  ]) as string | Date | null;

  const lines = findLinesFromRoots(roots);

  const partLines: PartnerOrderPartLineUi[] = lines.map((line, i) => {
    const L = (line && typeof line === "object" ? line : {}) as Record<string, unknown>;
    const partNested = asRecord(L.part);
    const catalogPart = asRecord(L.catalog_part) ?? asRecord(L.catalogPart);
    const product = asRecord(L.product) ?? asRecord(L.catalog_product);
    const modelAttrs =
      asRecord(L.model_attributes) ?? asRecord(L.modelAttributes);
    const cleanedModel =
      asRecord(L.cleaned_model) ?? asRecord(L.cleanedModel);

    const part = str(
      modelAttrs?.file_name ??
        modelAttrs?.fileName ??
        cleanedModel?.file_name ??
        cleanedModel?.original_filename ??
        modelAttrs?.model_code ??
        L.part_name ??
        L.partName ??
        L.product_name ??
        L.productName ??
        L.description ??
        L.name ??
        L.title ??
        partNested?.name ??
        partNested?.part_name ??
        partNested?.title ??
        catalogPart?.name ??
        catalogPart?.part_name ??
        product?.name ??
        product?.title ??
        product?.product_name,
    );

    const wfObj =
      L.workflow_template ??
      L.workflowTemplate ??
      L.workflow ??
      L.template;
    const wfRec =
      wfObj && typeof wfObj === "object" ? (wfObj as Record<string, unknown>) : null;

    let workflow = str(
      wfRec?.name ??
        wfRec?.template_name ??
        L.workflow_template_name ??
        L.workflowTemplateName ??
        L.workflow_name,
    );

    if (!workflow.trim()) workflow = "—";

    const qty = Number(
      L.quantity ??
        L.qty ??
        L.vendor_order_quantity ??
        L.vendorOrderQuantity ??
        L.order_quantity ??
        L.orderQuantity ??
        L.quantity_ordered ??
        L.quantityOrdered ??
        L.ordered_quantity ??
        L.orderedQuantity ??
        L.total_quantity ??
        0,
    );

    const batchNested = asRecord(L.batch);
    const batchName =
      str(L.batch_name ?? L.batchName ?? batchNested?.name ?? L.batch_number).trim() ||
      null;

    const jobTags: string[] = [];
    const tagSources = [
      L.job_tags,
      L.jobTags,
      L.job_labels,
      L.jobs,
      L.batches,
    ];
    for (const ts of tagSources) {
      if (Array.isArray(ts)) {
        for (const t of ts) {
          if (typeof t === "string") jobTags.push(t);
          else if (t && typeof t === "object") {
            const tr = t as Record<string, unknown>;
            const nm = str(tr.name ?? tr.label ?? tr.title ?? tr.batch_name);
            if (nm) jobTags.push(nm);
          }
        }
        if (jobTags.length) break;
      }
    }

    if (batchName && !jobTags.includes(batchName)) jobTags.push(batchName);

    const done = Number(
      L.quantity_produced ??
        L.quantityProduced ??
        L.produced_qty ??
        L.completed_quantity ??
        L.done ??
        0,
    );
    const total = Number.isFinite(qty) && qty > 0 ? qty : 0;

    return {
      key: `pl-${i}-${part}`,
      part: part || `Part ${i + 1}`,
      workflow: workflow || "—",
      qty: Number.isFinite(qty) ? qty : 0,
      batchName,
      jobTags,
      done: Number.isFinite(done) ? done : 0,
      total,
    };
  });

  const jobRowsRaw = findJobsFromRoots(roots);

  const jobs: PartnerOrderJobUi[] = jobRowsRaw.map((j, i) => {
    const J = (j && typeof j === "object" ? j : {}) as Record<string, unknown>;
    const name = str(
      J.name ?? J.job_name ?? J.jobName ?? J.title ?? J.label ?? `Job ${i + 1}`,
    );
    const status = mapJobStatus(str(J.status ?? J.job_status ?? J.state ?? "Open"));
    const tasksDone = Number(
      J.tasks_completed ?? J.tasksCompleted ?? J.completed_tasks ?? J.done_tasks ?? 0,
    );
    const tasksTotal = Number(
      J.tasks_total ?? J.tasksTotal ?? J.total_tasks ?? J.task_count ?? 0,
    );
    const recipe = str(J.recipe_name ?? J.recipe ?? J.recipeName);
    return {
      key: `jb-${i}-${name}`,
      name,
      status,
      tasksDone: Number.isFinite(tasksDone) ? tasksDone : 0,
      tasksTotal: Number.isFinite(tasksTotal) ? tasksTotal : 0,
      recipe: recipe || "No recipe",
    };
  });

  return {
    company: company || "—",
    contact: contact || "—",
    email,
    location: location || "—",
    issuedAt,
    dispatchDate,
    partLines,
    jobs,
  };
}
